import { create } from 'zustand'

function buildLocalAlertId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function normalizeRemoteAlert(alert) {
  return {
    id: alert.id,
    type: alert.type,
    severity: alert.severity || 'info',
    message: alert.message || '',
    timestamp: alert.timestamp || new Date().toISOString(),
    metadata: alert.metadata || null,
    local: false,
    persistent: alert.severity === 'error' || alert.severity === 'warning',
  }
}

function sortNewestFirst(items) {
  return [...items].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

function mergeDisplayAlerts(activeAlerts, toastQueue) {
  return sortNewestFirst([...(activeAlerts || []), ...(toastQueue || [])])
}

export const useAlertStore = create((set) => ({
  activeAlerts: [],
  toastQueue: [],
  acknowledgedAlertIds: [],
  lastReceivedAt: null,
  // Backward compatibility for existing dashboard selectors.
  alerts: [],

  deduplicateAlert: (alert) => {
    if (!alert?.id) return false
    const state = useAlertStore.getState()
    return (state.activeAlerts || []).some((row) => row.id === alert.id)
  },

  setActiveAlerts: (alerts) =>
    set((state) => {
      const existingLocal = (state.activeAlerts || []).filter((alert) => alert.local)
      const remoteAlerts = (Array.isArray(alerts) ? alerts : [])
        .map(normalizeRemoteAlert)
        .filter((alert) => !(state.acknowledgedAlertIds || []).includes(alert.id))
      const dedupedRemote = Array.from(
        new Map(remoteAlerts.map((alert) => [alert.id, alert])).values(),
      )

      const merged = sortNewestFirst([...existingLocal, ...dedupedRemote])
      return {
        activeAlerts: merged,
        alerts: mergeDisplayAlerts(merged, state.toastQueue),
        lastReceivedAt: new Date().toISOString(),
      }
    }),

  addToast: ({ type = 'info', severity = 'info', message, metadata = null, persistent = false }) =>
    set((state) => {
      const toastQueue = sortNewestFirst([
        {
          id: buildLocalAlertId(),
          type,
          severity,
          message: message || '',
          metadata,
          timestamp: new Date().toISOString(),
          local: true,
          persistent,
        },
        ...(state.toastQueue || []),
      ])
      return {
        toastQueue,
        alerts: mergeDisplayAlerts(state.activeAlerts, toastQueue),
      }
    }),

  acknowledgeAlertLocal: (id) =>
    set((state) => {
      if (!id) return state
      const acknowledged = new Set(state.acknowledgedAlertIds || [])
      acknowledged.add(id)
      const activeAlerts = (state.activeAlerts || []).filter((alert) => alert.id !== id)
      return {
        acknowledgedAlertIds: [...acknowledged],
        activeAlerts,
        alerts: mergeDisplayAlerts(activeAlerts, state.toastQueue),
      }
    }),

  removeToast: (id) =>
    set((state) => {
      const toastQueue = (state.toastQueue || []).filter((toast) => toast.id !== id)
      return {
        toastQueue,
        alerts: mergeDisplayAlerts(state.activeAlerts, toastQueue),
      }
    }),

  addAlert: (alert) =>
    set((state) => {
      if (!alert?.id) return state
      if ((state.activeAlerts || []).some((row) => row.id === alert.id)) return state
      const activeAlerts = sortNewestFirst([...(state.activeAlerts || []), alert])
      return { activeAlerts, alerts: mergeDisplayAlerts(activeAlerts, state.toastQueue) }
    }),

  // Merge remote alerts while preserving local client-side toasts.
  ingestRemoteAlerts: (alerts) =>
    useAlertStore.getState().setActiveAlerts(alerts),

  addLocalAlert: ({ type = 'info', severity = 'info', message, metadata = null, persistent = false }) =>
    useAlertStore.getState().addToast({ type, severity, message, metadata, persistent }),

  removeAlert: (id) =>
    set((state) => {
      if (!id) return state
      const acknowledged = new Set(state.acknowledgedAlertIds || [])
      acknowledged.add(id)
      const activeAlerts = (state.activeAlerts || []).filter((alert) => alert.id !== id)
      return {
        acknowledgedAlertIds: [...acknowledged],
        activeAlerts,
        alerts: mergeDisplayAlerts(activeAlerts, state.toastQueue),
        toastQueue: (state.toastQueue || []).filter((toast) => toast.id !== id),
      }
    }),

  resetAlerts: () => set({
    activeAlerts: [],
    toastQueue: [],
    acknowledgedAlertIds: [],
    lastReceivedAt: null,
    alerts: [],
  }),
}))
